import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import Logo from '@/components/Logo';
import EventCard from '@/components/EventCard';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  MessageCircle, 
  ClipboardList, 
  ArrowRight, 
  Calendar, 
  ShoppingBag,
  Sparkles
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/events`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const hikingEvents = events.filter(e => e.event_type === 'hiking');
  const gearShows = events.filter(e => e.event_type === 'gear_show' || e.event_type === 'workshop');

  const actionCards = [
    {
      icon: MessageCircle,
      title: 'Chat with a Mentor',
      description: 'Get personalized guidance from an experienced outdoor enthusiast',
      action: () => toast.info('Mentor chat coming soon!'),
      color: 'from-blue-500/20 to-blue-600/20',
      iconColor: 'text-blue-500'
    },
    {
      icon: ClipboardList,
      title: 'Take the Quiz',
      description: 'Help us understand your interests to find perfect matches',
      action: () => navigate('/quiz'),
      color: 'from-primary/20 to-accent/20',
      iconColor: 'text-primary',
      highlight: true
    },
    {
      icon: ArrowRight,
      title: 'Skip to Dashboard',
      description: 'Explore on your own and take the quiz later',
      action: () => navigate('/dashboard'),
      color: 'from-muted/50 to-muted/30',
      iconColor: 'text-muted-foreground'
    }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="onboarding-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Logo />
          <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="onboarding-skip-btn">
            Skip for now
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        {/* Welcome Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Sparkles className="h-4 w-4" />
            Welcome, {user?.name?.split(' ')[0] || 'Explorer'}!
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Let's Get You Started
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Explore local events, connect with mentors, or take our quick quiz to get personalized recommendations.
          </p>
        </div>

        {/* Action Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {actionCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <button
                key={card.title}
                onClick={card.action}
                className={`relative p-8 rounded-3xl border text-left transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${card.highlight ? 'border-primary bg-gradient-to-br ' + card.color : 'border-border/50 bg-card hover:border-primary/50'}`}
                data-testid={`onboarding-action-${index}`}
              >
                {card.highlight && (
                  <span className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                    Recommended
                  </span>
                )}
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-6`}>
                  <Icon className={`h-7 w-7 ${card.iconColor}`} />
                </div>
                <h3 className="text-xl font-semibold mb-2">{card.title}</h3>
                <p className="text-muted-foreground">{card.description}</p>
              </button>
            );
          })}
        </div>

        {/* Local Hiking Events */}
        {hikingEvents.length > 0 && (
          <section className="mb-16">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <Calendar className="h-6 w-6 text-primary" />
                <h2 className="text-2xl font-bold">Local Hiking Events</h2>
              </div>
              <Button variant="ghost" onClick={() => navigate('/discovery?type=hiking')} data-testid="view-all-hiking-btn">
                View All <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {hikingEvents.slice(0, 2).map((event) => (
                <EventCard key={event.event_id} event={event} />
              ))}
            </div>
          </section>
        )}

        {/* Gear Shows & Workshops */}
        {gearShows.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <ShoppingBag className="h-6 w-6 text-accent" />
                <h2 className="text-2xl font-bold">Gear Shows & Workshops</h2>
              </div>
              <Button variant="ghost" onClick={() => navigate('/discovery?type=gear')} data-testid="view-all-gear-btn">
                View All <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {gearShows.slice(0, 2).map((event) => (
                <EventCard key={event.event_id} event={event} />
              ))}
            </div>
          </section>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </main>
    </div>
  );
};

export default Onboarding;
